import React from "react";
import { useTranslation } from "react-i18next";

import { Identifiable, List, ProjectElementType } from "@planzone/dto";
import { useDuplicateList } from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { Loader } from "@common/loader";
import { Props } from "./types";

function getRank(parentList: Identifiable<List>, currentRank: number): number {
  const ranks = [
    ...parentList.subLists.map(({ activityKeyRank }) => activityKeyRank),
    ...parentList.tasks.map(({ activityKeyRank }) => activityKeyRank),
    ...parentList.milestones.map(({ keyRank }) => keyRank),
    ...parentList.events.map(({ keyRank }) => keyRank),
  ].sort();
  const index = ranks.findIndex((rank) => currentRank);

  return index !== -1
    ? (currentRank +
        (typeof ranks[index + 1] !== "undefined"
          ? ranks[index + 1]
          : currentRank + 1)) /
        2
    : currentRank + 1;
}

function ContextualMenu({ list, parentList, project, addAfter, addIn }: Props) {
  const { t } = useTranslation();
  const { isRequestPending } = useDuplicateList(list);
  const { pushView } = useModal();

  return (
    <SecureContextualMenu
      context={{ list }}
      items={[
        {
          name: t("listsList.contextualMenu.edit"),
          useCase: UseCases.editList,
          act: () =>
            pushView({
              key: UseCases.retrieveList,
              data: { listId: list.uuid },
            }),
        },
        {
          name: t("listsList.contextualMenu.move"),
          useCase: UseCases.moveListElement,
          act: () =>
            pushView({
              key: UseCases.moveListElement,
              data: {
                projectElementId: list.uuid,
                projectElementType: ProjectElementType.LIST,
                currentParentList: parentList,
                project,
              },
            }),
        },
        {
          name: t("listsList.contextualMenu.addIn"),
          useCases: [
            UseCases.createTask,
            UseCases.createList,
            UseCases.createMilestone,
            UseCases.createMilestone,
          ],
          act: addIn,
        },
        {
          name: t("listsList.contextualMenu.addAfter"),
          useCases: [
            UseCases.createTask,
            UseCases.createList,
            UseCases.createMilestone,
            UseCases.createMilestone,
          ],
          act: addAfter,
        },
        {
          name: t("listsList.contextualMenu.duplicate"),
          useCases: [UseCases.duplicateList],
          renderIcon: (className) =>
            isRequestPending ? (
              <div className={className}>
                <Loader />
              </div>
            ) : (
              <React.Fragment />
            ),
          act: () =>
            !isRequestPending &&
            pushView({
              key: UseCases.duplicateList,
              data: {
                list,
                rank: getRank(parentList, list.activityKeyRank),
              },
            }),
        },
        {
          name: t("listsList.contextualMenu.remove"),
          color: "#DB5F63FF",
          useCase: UseCases.removeList,
          act: () =>
            pushView({
              key: UseCases.removeList,
              data: { list },
            }),
        },
      ]}
    />
  );
}

export { ContextualMenu };
